import { useEffect, useRef, useState, type CSSProperties, type JSX, type PointerEvent as ReactPointerEvent, type RefObject } from "react";

import type { EventDTO } from "../../../common/api/types.js";
import { formatTime, isSameDay, occursOn } from "../weekRange.js";

const HOURS = Array.from({ length: 17 }, (_, index) => index + 6);
const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const DAY_ROW_HEIGHT = 56;

interface CalendarViewProps {
  events: EventDTO[];
  anchor: Date;
  days: Date[];
  onCreateRange: (start: Date, end: Date) => void;
  onChangeEvent: (eventId: string, startsAt: Date, endsAt: Date) => void;
  onSelect: (eventId: string) => void;
  onPickDay: (date: Date) => void;
}

export function CalendarOverview({ events, days, onSelect }: CalendarViewProps): JSX.Element {
  const groups = days
    .map((day) => ({ day, events: eventsForDay(events, day) }))
    .filter((group) => group.events.length > 0);

  if (groups.length === 0) {
    return <EmptyCalendar text="Keine Termine in den nächsten 30 Tagen." />;
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 pb-6">
      {groups.map(({ day, events: dayEvents }) => (
        <section key={dateKey(day)}>
          <div className="mb-1.5 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-brand">{day.getDate()}</span>
            <h3 className="text-xs font-semibold text-ink-muted">
              {day.toLocaleDateString("de-DE", { weekday: "long", month: "long" })}
              {isSameDay(day, new Date()) ? " · Heute" : ""}
            </h3>
          </div>
          <div className="ml-3 border-l border-line pl-3">
            {dayEvents.map((event) => (
              <button
                key={event.id}
                type="button"
                onClick={() => onSelect(event.id)}
                className="flex w-full items-center gap-3 border-b border-line py-3 text-left last:border-b-0 hover:bg-surface-sunken"
              >
                <span className="h-10 w-1 shrink-0 rounded-full" style={{ backgroundColor: event.calendarColor }} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{event.title}</span>
                  <span className="block text-xs text-ink-muted">{eventTime(event)}</span>
                  {event.location ? <span className="block truncate text-xs text-ink-muted">{event.location}</span> : null}
                </span>
                <span className="text-xs text-ink-muted">{event.calendarName}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function CalendarDayView({ events, anchor, onCreateRange, onChangeEvent, onSelect }: CalendarViewProps): JSX.Element {
  const dayEvents = eventsForDay(events, anchor);
  const fixed = dayEvents.filter((event) => event.allDay || spansDays(event));
  const timed = dayEvents.filter((event) => !event.allDay && !spansDays(event));
  const grid = useRef<HTMLDivElement>(null);

  return (
    <div className="mx-auto w-full max-w-4xl px-4 pb-6">
      {fixed.length > 0 ? (
        <div className="sticky top-0 z-10 mb-3 space-y-1 border-b border-line bg-surface-raised py-2">
          {fixed.map((event) => (
            <CompactEvent key={event.id} event={event} onSelect={onSelect} />
          ))}
        </div>
      ) : null}
      <div ref={grid} className="relative" style={{ height: HOURS.length * DAY_ROW_HEIGHT }}>
        {HOURS.map((hour) => (
          <div
            key={hour}
            className="grid w-full grid-cols-[48px_1fr] border-t border-line text-left"
            style={{ height: DAY_ROW_HEIGHT }}
          >
            <span className="pr-2 pt-1 text-right text-[11px] text-ink-muted">{String(hour).padStart(2, "0")}:00</span>
            <span />
          </div>
        ))}
        <TimeRangeSelector day={anchor} className="absolute bottom-0 left-[48px] right-0 top-0" onCreateRange={onCreateRange} />
        {timed.map((event) => (
          <InteractiveEvent
            key={event.id}
            event={event}
            grid={grid}
            dayCount={1}
            className="absolute left-[54px] right-2 z-10"
            style={eventPixelStyle(event, anchor, DAY_ROW_HEIGHT)}
            onSelect={onSelect}
            onChange={onChangeEvent}
          />
        ))}
      </div>
    </div>
  );
}

export function CalendarWeekView({ events, days, onCreateRange, onChangeEvent, onSelect }: CalendarViewProps): JSX.Element {
  const spanning = events.filter((event) => event.allDay || spansDays(event));
  const grid = useRef<HTMLDivElement>(null);
  return (
    <div className="flex h-full min-h-[620px] flex-col px-1 pb-3">
      <div className="grid grid-cols-[42px_repeat(7,1fr)] border-b border-line pb-1 text-center">
        <span />
        {days.map((day, index) => (
          <span key={dateKey(day)} className={isSameDay(day, new Date()) ? "text-brand" : "text-ink-muted"}>
            <span className="block text-[10px] font-semibold">{WEEKDAYS[index]}</span>
            <span className={["mx-auto grid h-6 w-6 place-items-center text-sm font-extrabold", isSameDay(day, new Date()) ? "rounded-full bg-brand text-brand-ink" : ""].join(" ")}>{day.getDate()}</span>
          </span>
        ))}
      </div>
      {spanning.length > 0 ? (
        <div className="grid grid-cols-[42px_1fr] border-b border-line py-1">
          <span className="pr-1 text-right text-[9px] font-bold text-ink-muted">GANZTAG</span>
          <div className="grid grid-cols-7 gap-y-1">
            {spanning.map((event) => {
              const segment = eventWeekSegment(event, days[0] as Date);
              return <button key={event.id} type="button" onClick={() => onSelect(event.id)} className="mx-0.5 truncate rounded border border-l-2 px-1.5 py-1 text-left text-[9px] font-bold text-ink" style={{ ...eventToneStyle(event), gridColumn: `${segment.start} / ${segment.end}` }}>{event.title}</button>;
            })}
          </div>
        </div>
      ) : null}
      <div className="grid min-h-0 flex-1 grid-cols-[42px_1fr] overflow-hidden">
        <div className="grid" style={{ gridTemplateRows: `repeat(${HOURS.length}, 1fr)` }}>
          {HOURS.map((hour) => <span key={hour} className="pr-1 text-right text-[10px] font-semibold text-ink-muted">{String(hour).padStart(2, "0")}</span>)}
        </div>
        <div ref={grid} className="grid bg-[linear-gradient(to_bottom,rgb(var(--line))_0_1px,transparent_1px)]" style={{ backgroundSize: `100% calc(100% / ${HOURS.length})`, gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
          {days.map((day) => (
            <div key={dateKey(day)} className="relative border-l border-line">
              <TimeRangeSelector day={day} className="absolute inset-0" onCreateRange={onCreateRange} />
              {eventsForDay(events, day).filter((event) => !event.allDay && !spansDays(event)).map((event) => (
                <InteractiveEvent
                  key={event.id}
                  event={event}
                  grid={grid}
                  dayCount={7}
                  className="absolute left-0.5 right-0.5 z-10"
                  style={eventPercentStyle(event)}
                  compact
                  onSelect={onSelect}
                  onChange={onChangeEvent}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CalendarMonthView({ events, anchor, days, onSelect, onPickDay }: CalendarViewProps): JSX.Element {
  return (
    <div className="flex h-full min-h-[500px] flex-col pb-3">
      <div className="grid grid-cols-7 border-b border-line px-1 pb-1 text-center text-[11px] font-bold text-ink-muted">
        {WEEKDAYS.map((weekday) => <span key={weekday}>{weekday}</span>)}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6">
        {days.map((day) => {
          const dayEvents = eventsForDay(events, day);
          return (
            <div key={dateKey(day)} className={["relative min-w-0 overflow-hidden border-b border-r border-line px-1 pt-7", day.getMonth() !== anchor.getMonth() ? "bg-surface-sunken text-ink-muted" : "bg-surface-raised"].join(" ")}>
              <button
                type="button"
                onClick={() => onPickDay(day)}
                aria-label={`${day.toLocaleDateString("de-DE")} in der Tagesansicht öffnen`}
                className={[
                  "absolute left-1/2 top-1 grid h-5 w-5 -translate-x-1/2 place-items-center text-[11px] font-extrabold",
                  isSameDay(day, new Date()) ? "bg-brand text-brand-ink" : "text-ink",
                ].join(" ")}
              >
                {day.getDate()}
              </button>
              {dayEvents.slice(0, 2).map((event) => (
                <button key={event.id} type="button" onClick={() => onSelect(event.id)} className={["mb-0.5 block w-full truncate border py-0.5 text-left text-[9px] font-bold text-ink", monthEventClass(event, day)].join(" ")} style={eventToneStyle(event)}>{showMonthEventTitle(event, day) ? event.title : "\u00a0"}</button>
              ))}
              {dayEvents.length > 2 ? <span className="block pl-1 text-[9px] font-bold text-ink-muted">+{dayEvents.length - 2}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeRangeSelector({ day, className, onCreateRange }: {
  day: Date;
  className: string;
  onCreateRange: (start: Date, end: Date) => void;
}): JSX.Element {
  const [selection, setSelection] = useState<{ start: Date; end: Date } | null>(null);
  const gesture = useRef<{
    pointerId: number;
    originX: number;
    originY: number;
    start: Date;
    active: boolean;
    target: HTMLButtonElement;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);

  useEffect(() => () => {
    if (gesture.current?.timer) clearTimeout(gesture.current.timer);
  }, []);

  const activate = (): void => {
    const current = gesture.current;
    if (!current) return;
    current.active = true;
    current.target.setPointerCapture(current.pointerId);
    setSelection({ start: current.start, end: new Date(current.start.getTime() + 30 * 60_000) });
  };

  const start = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const target = event.currentTarget;
    const startAt = dateAtPointer(day, event.clientY, target.getBoundingClientRect());
    gesture.current = {
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
      start: startAt,
      active: false,
      target,
      timer: null,
    };
    if (event.pointerType === "touch") {
      gesture.current.timer = setTimeout(activate, 350);
    } else {
      activate();
    }
  };

  const move = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (!current.active) {
      if (Math.hypot(event.clientX - current.originX, event.clientY - current.originY) > 8) cancelRangeSelection();
      return;
    }
    event.preventDefault();
    const pointedAt = dateAtPointer(day, event.clientY, current.target.getBoundingClientRect());
    setSelection(normalizeRange(current.start, pointedAt));
  };

  const finish = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const current = gesture.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (current.timer) clearTimeout(current.timer);
    if (current.active) {
      const range = normalizeRange(current.start, dateAtPointer(day, event.clientY, current.target.getBoundingClientRect()));
      onCreateRange(range.start, range.end);
    } else {
      onCreateRange(current.start, new Date(current.start.getTime() + 60 * 60_000));
    }
    gesture.current = null;
    setSelection(null);
  };

  const cancelRangeSelection = (): void => {
    const current = gesture.current;
    if (current?.timer) clearTimeout(current.timer);
    gesture.current = null;
    setSelection(null);
  };

  return (
    <button
      type="button"
      aria-label={`Zeit am ${day.toLocaleDateString("de-DE")} auswählen`}
      className={`${className} cursor-crosshair touch-pan-y select-none`}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={finish}
      onPointerCancel={cancelRangeSelection}
    >
      {selection ? <span className="pointer-events-none absolute left-0.5 right-0.5 rounded border-2 border-brand bg-brand/15" style={rangePercentStyle(selection.start, selection.end)} /> : null}
    </button>
  );
}

function InteractiveEvent({ event, grid, dayCount, className, style, compact = false, onSelect, onChange }: {
  event: EventDTO;
  grid: RefObject<HTMLDivElement | null>;
  dayCount: number;
  className: string;
  style: CSSProperties;
  compact?: boolean;
  onSelect: (eventId: string) => void;
  onChange: (eventId: string, startsAt: Date, endsAt: Date) => void;
}): JSX.Element {
  const [adjustment, setAdjustment] = useState<{ days: number; minutes: number; mode: "move" | "resize" } | null>(null);
  const gesture = useRef<{
    pointerId: number;
    originX: number;
    originY: number;
    active: boolean;
    moved: boolean;
    days: number;
    minutes: number;
    mode: "move" | "resize";
    target: HTMLElement;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const suppressClick = useRef(false);
  const canEdit = event.level !== "VIEW";

  useEffect(() => () => {
    if (gesture.current?.timer) clearTimeout(gesture.current.timer);
  }, []);

  const activate = (): void => {
    const current = gesture.current;
    if (!current) return;
    current.active = true;
    current.target.setPointerCapture(current.pointerId);
    setAdjustment({ days: 0, minutes: 0, mode: current.mode });
  };

  const startGesture = (mode: "move" | "resize") => (pointer: ReactPointerEvent<HTMLElement>): void => {
    pointer.stopPropagation();
    if (!canEdit || (pointer.pointerType === "mouse" && pointer.button !== 0)) return;
    gesture.current = {
      pointerId: pointer.pointerId,
      originX: pointer.clientX,
      originY: pointer.clientY,
      active: false,
      moved: false,
      days: 0,
      minutes: 0,
      mode,
      target: pointer.currentTarget,
      timer: null,
    };
    if (pointer.pointerType === "touch") {
      gesture.current.timer = setTimeout(activate, 350);
    } else {
      activate();
    }
  };

  const moveGesture = (pointer: ReactPointerEvent<HTMLElement>): void => {
    const current = gesture.current;
    const bounds = grid.current?.getBoundingClientRect();
    if (!current || !bounds || current.pointerId !== pointer.pointerId) return;
    const distance = Math.hypot(pointer.clientX - current.originX, pointer.clientY - current.originY);
    if (!current.active) {
      if (distance > 8) cancelGesture();
      return;
    }
    pointer.preventDefault();
    const minutes = snapMinutes((pointer.clientY - current.originY) / bounds.height * HOURS.length * 60);
    const days = current.mode === "move" && dayCount > 1
      ? Math.round((pointer.clientX - current.originX) / (bounds.width / dayCount))
      : 0;
    current.moved = current.moved || minutes !== 0 || days !== 0;
    current.days = days;
    current.minutes = minutes;
    setAdjustment({ days, minutes, mode: current.mode });
  };

  const finishGesture = (pointer: ReactPointerEvent<HTMLElement>): void => {
    const current = gesture.current;
    if (!current || current.pointerId !== pointer.pointerId) return;
    if (current.timer) clearTimeout(current.timer);
    if (current.active && current.moved) {
      const startsAt = shiftDate(new Date(event.startsAt), current.mode === "move" ? current.days : 0, current.mode === "move" ? current.minutes : 0);
      const proposedEnd = shiftDate(new Date(event.endsAt), current.mode === "move" ? current.days : 0, current.minutes);
      const endsAt = proposedEnd.getTime() - startsAt.getTime() < 30 * 60_000
        ? new Date(startsAt.getTime() + 30 * 60_000)
        : proposedEnd;
      suppressClick.current = true;
      onChange(event.id, startsAt, endsAt);
      setTimeout(() => { suppressClick.current = false; }, 500);
    }
    gesture.current = null;
    setAdjustment(null);
  };

  const cancelGesture = (): void => {
    const current = gesture.current;
    if (current?.timer) clearTimeout(current.timer);
    gesture.current = null;
    setAdjustment(null);
  };

  const adjustedStyle = adjustmentStyle(style, adjustment, grid.current?.getBoundingClientRect().height ?? 0);

  return (
    <div className={`${className} group overflow-hidden rounded border border-l-[3px] text-ink shadow-sm`} style={{ ...adjustedStyle, ...eventToneStyle(event) }}>
      <button
        type="button"
        className={["h-full w-full touch-none select-none text-left", compact ? "px-1 py-0.5 text-[9px] font-bold leading-tight" : "px-3 py-1.5"].join(" ")}
        onPointerDown={startGesture("move")}
        onPointerMove={moveGesture}
        onPointerUp={finishGesture}
        onPointerCancel={cancelGesture}
        onClick={(click) => {
          if (suppressClick.current) {
            click.preventDefault();
            suppressClick.current = false;
          } else {
            onSelect(event.id);
          }
        }}
      >
        <span className="block truncate font-semibold">{event.title}</span>
        {!compact ? <span className="block text-[11px] text-ink-muted">{eventTime(event)}</span> : null}
      </button>
      {canEdit ? (
        <button
          type="button"
          aria-label={`${event.title} in der Dauer ändern`}
          className="absolute bottom-0 left-1/2 h-2.5 w-10 -translate-x-1/2 cursor-ns-resize touch-none rounded-t bg-current opacity-20 sm:opacity-0 sm:group-hover:opacity-25 sm:group-focus-within:opacity-25"
          onPointerDown={startGesture("resize")}
          onPointerMove={moveGesture}
          onPointerUp={finishGesture}
          onPointerCancel={cancelGesture}
        />
      ) : null}
    </div>
  );
}

function CompactEvent({ event, onSelect, small = false }: { event: EventDTO; onSelect: (eventId: string) => void; small?: boolean }): JSX.Element {
  return (
    <button type="button" onClick={() => onSelect(event.id)} className={[
      "block w-full truncate rounded border border-l-4 text-left font-semibold text-ink shadow-sm",
      small ? "px-1 py-0.5 text-[9px]" : "px-2.5 py-1.5 text-xs",
    ].join(" ")} style={eventToneStyle(event)}>
      {event.title}
    </button>
  );
}

function EmptyCalendar({ text }: { text: string }): JSX.Element {
  return <p className="mx-4 rounded-xl border border-dashed border-line bg-surface-raised p-6 text-center text-sm font-medium text-ink-muted">{text}</p>;
}

function eventsForDay(events: readonly EventDTO[], day: Date): EventDTO[] {
  return events
    .filter((event) => occursOn(day, event.startsAt, event.endsAt))
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

function spansDays(event: EventDTO): boolean {
  const end = new Date(new Date(event.endsAt).getTime() - 1);
  return !isSameDay(new Date(event.startsAt), end);
}

function eventTime(event: EventDTO): string {
  return event.allDay ? "ganztägig" : `${formatTime(event.startsAt)}–${formatTime(event.endsAt)}`;
}

function eventPixelStyle(event: EventDTO, day: Date, rowHeight: number): CSSProperties {
  const dayStart = new Date(day);
  dayStart.setHours(6, 0, 0, 0);
  const startMinutes = Math.max(0, (new Date(event.startsAt).getTime() - dayStart.getTime()) / 60_000);
  const durationMinutes = Math.max(30, (new Date(event.endsAt).getTime() - new Date(event.startsAt).getTime()) / 60_000);
  return {
    top: startMinutes / 60 * rowHeight,
    height: Math.max(24, durationMinutes / 60 * rowHeight - 2),
  };
}

function eventPercentStyle(event: EventDTO): CSSProperties {
  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt);
  const startHour = startsAt.getHours() + startsAt.getMinutes() / 60;
  const endHour = endsAt.getHours() + endsAt.getMinutes() / 60;
  return {
    top: `${Math.max(0, (startHour - 6) / HOURS.length * 100)}%`,
    height: `${Math.max(4, (endHour - startHour) / HOURS.length * 100)}%`,
  };
}

function dateAtPointer(day: Date, clientY: number, bounds: DOMRect): Date {
  const rawMinutes = (clientY - bounds.top) / bounds.height * HOURS.length * 60;
  const minutes = Math.max(0, Math.min(HOURS.length * 60, snapMinutes(rawMinutes)));
  const result = new Date(day);
  result.setHours(6, minutes, 0, 0);
  return result;
}

function normalizeRange(first: Date, second: Date): { start: Date; end: Date } {
  const start = new Date(Math.min(first.getTime(), second.getTime()));
  const end = new Date(Math.max(first.getTime(), second.getTime()));
  if (end.getTime() - start.getTime() < 30 * 60_000) end.setTime(start.getTime() + 30 * 60_000);
  return { start, end };
}

function rangePercentStyle(start: Date, end: Date): CSSProperties {
  const startMinutes = start.getHours() * 60 + start.getMinutes() - 6 * 60;
  const duration = end.getTime() - start.getTime();
  return {
    top: `${startMinutes / (HOURS.length * 60) * 100}%`,
    height: `${duration / 60_000 / (HOURS.length * 60) * 100}%`,
  };
}

function snapMinutes(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

function shiftDate(date: Date, days: number, minutes: number): Date {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  shifted.setMinutes(shifted.getMinutes() + minutes);
  return shifted;
}

function adjustmentStyle(
  style: CSSProperties,
  adjustment: { days: number; minutes: number; mode: "move" | "resize" } | null,
  gridHeight: number,
): CSSProperties {
  if (!adjustment) return style;
  if (adjustment.mode === "move") {
    return {
      ...style,
      transform: `translate(${adjustment.days * 100}%, ${adjustment.minutes / (HOURS.length * 60) * gridHeight}px)`,
      opacity: 0.82,
    };
  }
  const minuteHeight = adjustment.minutes / (HOURS.length * 60) * gridHeight;
  const baseHeight = typeof style.height === "number"
    ? style.height
    : parseFloat(String(style.height)) / 100 * gridHeight;
  return { ...style, height: Math.max(24, baseHeight + minuteHeight), opacity: 0.82 };
}

function eventToneStyle(event: EventDTO): CSSProperties {
  return {
    backgroundColor: `color-mix(in srgb, ${event.calendarColor} 14%, rgb(var(--surface-raised)))`,
    borderColor: event.calendarColor,
  };
}

function eventWeekSegment(event: EventDTO, weekStart: Date): { start: number; end: number } {
  const last = new Date(new Date(event.endsAt).getTime() - 1);
  const startOffset = Math.max(0, wholeDaysBetween(weekStart, new Date(event.startsAt)));
  const endOffset = Math.min(6, wholeDaysBetween(weekStart, last));
  return { start: startOffset + 1, end: endOffset + 2 };
}

function wholeDaysBetween(left: Date, right: Date): number {
  const from = new Date(left); from.setHours(0, 0, 0, 0);
  const to = new Date(right); to.setHours(0, 0, 0, 0);
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

function monthEventClass(event: EventDTO, date: Date): string {
  if (!spansDays(event)) return "rounded border-l-2 px-1";
  const last = new Date(new Date(event.endsAt).getTime() - 1);
  const startsHere = isSameDay(new Date(event.startsAt), date) || date.getDay() === 1;
  const endsHere = isSameDay(last, date) || date.getDay() === 0;
  return `${startsHere ? "ml-0.5 rounded-l border-l-2 pl-1" : "-ml-1 border-l-0 pl-1"} ${endsHere ? "mr-0.5 rounded-r pr-1" : "-mr-1 border-r-0 pr-1"}`;
}

function showMonthEventTitle(event: EventDTO, date: Date): boolean {
  return isSameDay(new Date(event.startsAt), date) || date.getDay() === 1;
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
